import { createSlice, type PayloadAction } from "@reduxjs/toolkit"
import { emptyPersonIdentity, type PersonIdentityDraft, type createPersonPayload } from "@/lib/person-identity"
import { relationshipLabels } from "@/lib/benefit-enums"
import { staffSessionActions } from "@/store/staffSessionSlice"

export interface HouseholdCopyConfirmation {
  sourceConcurrencyToken: string
  householdRelationships: { personId: string; relationship: number }[]
}
export interface NewBenefitRequest extends Partial<HouseholdCopyConfirmation> {
  applicantId: string
  benefitApplicationType?: number
  copyHouseholdFromBenefitApplicationId?: string
  applicantLiheapEligibility?: number
}
export interface CopyDraft {
  id: string; sourceId: string; applicantId: string
  baseline: { token: string; members: { id: string; firstName: string; lastName: string; middleName?: string }[] } | null
  relationships: Record<string, string>
}
type CreationCommand =
  | { kind: "person"; body: ReturnType<typeof createPersonPayload>; liheapEligibility: number }
  | { kind: "application"; body: NewBenefitRequest }
export type CreationAttempt = CreationCommand & {
  id: string; scopeKey: string; epoch: number
  phase: "saving" | "rejected" | "conflict" | "outcomeUnknown" | "abandonedUnknown" | "confirmed"
  resultId: string | null
}
export interface BenefitCreationState {
  scopeKey: string | null
  mode: "find" | "create"
  searchText: string
  submittedSearch: string
  identity: PersonIdentityDraft
  liheapEligibility: string
  applicantEligibility: { personId: string; value: number } | null
  previousYearsFor: string | null
  copy: CopyDraft | null
  attempt: CreationAttempt | null
  message: string | null
  fieldErrors: Record<string, string[]>
}
const initial = (): BenefitCreationState => ({ scopeKey: null, mode: "find", searchText: "", submittedSearch: "",
  identity: { ...emptyPersonIdentity }, liheapEligibility: "255", applicantEligibility: null,
  previousYearsFor: null, copy: null, attempt: null, message: null, fieldErrors: {} })
export const creationLocked = (state: BenefitCreationState) => !!state.attempt && state.attempt.phase !== "rejected"
export const creationNavigationBlocked = (state: BenefitCreationState) =>
  !!state.copy || ["saving", "conflict", "outcomeUnknown"].includes(state.attempt?.phase ?? "") ||
  ((!state.attempt || state.attempt.phase === "rejected") && Object.entries(state.identity).some(([key, value]) => value !== emptyPersonIdentity[key as keyof PersonIdentityDraft]))

/** Captured roster/token, not the latest query result. Never rebase a confirmation implicitly. */
export function copyConfirmation(copy: CopyDraft | null): HouseholdCopyConfirmation | null {
  if (!copy?.baseline?.token || !copy.baseline.members.length || new Set(copy.baseline.members.map(p => p.id)).size !== copy.baseline.members.length) return null
  if (!copy.baseline.members.some(p => p.id === copy.applicantId)) return null
  const rows = copy.baseline.members.map(p => ({ personId: p.id, relationship: Number(copy.relationships[p.id]) }))
  if (rows.some(row => !Object.hasOwn(copy.relationships, row.personId) || !Object.hasOwn(relationshipLabels, row.relationship)
    || (row.relationship === 0) !== (row.personId === copy.applicantId))) return null
  return { sourceConcurrencyToken: copy.baseline.token, householdRelationships: rows }
}
const slice = createSlice({ name: "benefitCreation", initialState: initial(), reducers: {
  fieldsChanged(state, { payload }: PayloadAction<Partial<Pick<BenefitCreationState, "mode" | "searchText" | "submittedSearch" | "identity" | "liheapEligibility" | "previousYearsFor">>>) {
    if (!state.scopeKey || creationLocked(state)) return
    Object.assign(state, payload)
  },
  applicantSelected(state) {
    if (creationLocked(state)) return
    state.identity = { ...emptyPersonIdentity }; state.applicantEligibility = null; state.copy = null
    state.attempt = null; state.message = null; state.fieldErrors = {}
  },
  copyOpened(state, { payload }: PayloadAction<Pick<CopyDraft, "id" | "sourceId" | "applicantId">>) {
    if (!state.scopeKey || creationLocked(state)) return
    state.copy = { ...payload, baseline: null, relationships: { [payload.applicantId]: "0" } }
  },
  copyLoaded(state, { payload }: PayloadAction<{ id: string; baseline: NonNullable<CopyDraft["baseline"]> }>) {
    if (state.copy?.id === payload.id && !state.copy.baseline && !creationLocked(state)) state.copy.baseline = payload.baseline
  },
  relationshipChanged(state, { payload }: PayloadAction<{ id: string; personId: string; value: string }>) {
    if (state.copy?.id === payload.id && !creationLocked(state) && payload.personId !== state.copy.applicantId) state.copy.relationships[payload.personId] = payload.value
  },
  copyClosed(state) { if (!creationLocked(state)) state.copy = null },
  attemptRequested(state, { payload }: PayloadAction<CreationAttempt>) {
    if (state.scopeKey !== payload.scopeKey || creationLocked(state) || payload.phase !== "saving") return
    if (payload.kind === "application" && state.copy) {
      const confirmation = copyConfirmation(state.copy)
      if (!confirmation || payload.body.applicantId !== state.copy.applicantId || payload.body.copyHouseholdFromBenefitApplicationId !== state.copy.sourceId
        || payload.body.sourceConcurrencyToken !== confirmation.sourceConcurrencyToken
        || JSON.stringify(payload.body.householdRelationships) !== JSON.stringify(confirmation.householdRelationships)) return
    }
    state.attempt = payload; state.message = null; state.fieldErrors = {}
  },
  attemptConfirmed(state, { payload }: PayloadAction<{ id: string; resultId: string }>) {
    if (state.attempt?.id !== payload.id || state.attempt.phase !== "saving") return
    state.attempt.phase = "confirmed"; state.attempt.resultId = payload.resultId; state.copy = null
  },
  personAdopted(state, { payload }: PayloadAction<string>) {
    const attempt = state.attempt
    if (attempt?.kind !== "person" || attempt.phase !== "confirmed" || attempt.resultId !== payload) return
    state.applicantEligibility = { personId: payload, value: attempt.liheapEligibility }
    state.identity = { ...emptyPersonIdentity }; state.attempt = null
  },
  attemptRejected(state, { payload }: PayloadAction<{ id: string; outcome: "notSent" | "validation" | "conflict" | "unknown" | "denied"; message: string; fieldErrors: Record<string, string[]> }>) {
    if (state.attempt?.id !== payload.id || state.attempt.phase !== "saving") return
    if (payload.outcome === "denied") return { ...initial(), scopeKey: state.scopeKey }
    state.attempt.phase = payload.outcome === "unknown" ? "outcomeUnknown" : payload.outcome === "conflict" ? "conflict" : "rejected"
    state.message = payload.message; state.fieldErrors = payload.fieldErrors
  },
  discardConfirmed(state) {
    if (state.attempt && !["rejected", "conflict", "confirmed"].includes(state.attempt.phase)) return
    return { ...initial(), scopeKey: state.scopeKey }
  },
  unknownExitAcknowledged(state) {
    if (state.attempt?.phase !== "outcomeUnknown") return
    state.attempt.phase = "abandonedUnknown"; state.copy = null
  },
  unknownResolutionAcknowledged(state) {
    if (state.attempt?.phase === "abandonedUnknown") return { ...initial(), scopeKey: state.scopeKey }
  },
}, extraReducers: builder => {
  builder.addCase(staffSessionActions.scopeRevoked, () => initial())
  builder.addCase(staffSessionActions.scopeEstablished, (state, { payload }) => {
    if (state.scopeKey !== payload) return { ...initial(), scopeKey: payload }
  })
  builder.addCase(staffSessionActions.contextAccepted, (state, { payload }) => {
    if (state.scopeKey !== payload.scopeKey) return { ...initial(), scopeKey: payload.scopeKey }
  })
} })
export const benefitCreationActions = slice.actions
export default slice.reducer
